<template>
  <div>
    <admin-add-coowner-dialog
      :changeSelectedPeopleInfo="changeSelectedPeopleInfo"
      :peopleSearchDialog="peopleSearchDialog"
      :toggleModal="toggleModal"
      :selectedPeopleList="owners"
      v-on:personAdded="($event) => this.$emit('personAdded', { personId: $event.personId })"
      />
    <h3 v-if="!hideHeader" >
      {{ title(ownershipType) }}(s)
      <span v-if="!isReadOnlyMode">
        <v-btn :disabled="!isEditMode" v-if="!!owners && owners.length" v-on:click="toggleModal()" class="primary"> 
          Edit 
        </v-btn>
        <v-btn :disabled="!isEditMode" v-else v-on:click="toggleModal()"  class="primary">
            <font-awesome-icon icon="plus" />
            Add {{ title(ownershipType) }}
        </v-btn>
      </span>  
    </h3>
    <div v-if="!isReadOnlyMode && !!owners && owners.length">
      <v-radio-group :disabled="!isEditMode" v-model="primaryLessee" @change="changePrimaryLessee">
        <span v-for="co in owners" :key="co.personId">
          <span>
            <v-layout align-start row class="mb-3">
              <v-radio color="primary" :value="co.personId">
                <template v-slot:label
                  ><span v-if="co.isPrimary">
                    <v-tooltip dark right>
                      <template v-slot:activator="{on, attrs }">
                        <font-awesome-icon
                          icon="paw"
                          color="primary"
                          v-bind="attrs"
                          v-on="on"
                        />&nbsp;
                      </template>
                      <span>
                        &nbsp;"Only the primary lessee can register litters,
                        release and register puppies. They will also be a
                        primary contact person for the ASCA office."
                      </span>
                    </v-tooltip>
                  </span>
                  <span v-else>
                    <font-awesome-icon icon="paw" color="transparent" />&nbsp;
                  </span>
                <span>
                  <b>
                    <a :href="`/admin/people/${co.personId}`" target="_blank">
                    {{ co.firstName }} {{ co.lastName }}
                    </a>
                    <span v-if="co.memberId">&nbsp;(Member ID: {{ co.memberId }})</span>
                  </b>
                <br />
                <span v-if="!hideContactInfo">
                  <span v-if="co.email">{{
                    co.email
                  }}</span
                  ><span v-else>No email</span>
                  <br />
                  <span v-if="co.phoneNumber">{{
                    co.phoneNumber
                  }}</span
                  ><span v-else>No phone</span>
                  </span>
                </span> 
                </template>
              </v-radio>
            </v-layout>
          </span>
        </span>
      </v-radio-group>
    </div>
    <div v-if="isReadOnlyMode && !!owners && owners.length">
        <span v-for="(co, i) in owners" :key="i">
        <span v-if="co.isPrimary">
          <v-tooltip dark right>
            <template v-slot:activator="{ on, attrs }">
              <font-awesome-icon icon="paw" color="primary"
                v-bind="attrs" v-on="on" />
            </template>
            <span>{{ tooltipText(ownershipType) }}</span>
          </v-tooltip>
        </span>
        <span v-else>
          <font-awesome-icon icon="paw" color="transparent" />
        </span>
        <b>
          &nbsp;
          <a :href="`/admin/people/${co.personId}`" target="_blank">
          {{ co.firstName }} {{ co.lastName }}
          </a>
          <span v-if="co.memberId">&nbsp;(Member ID: {{ co.memberId }}<span v-if="co.membershipDuration"> for {{co.membershipDuration}} years</span>)</span>
        </b>
        <br />
        <span v-if="!hideContactInfo">
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span v-if="co.email">{{
            co.email
          }}</span
          ><span v-else>No email</span>
          <br />
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;<span v-if="co.phoneNumber">{{
            co.phoneNumber
          }}</span
          ><span v-else>No phone</span>
          <br />
        </span>  
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import AdminAddCoownerDialog from "../Admin/AdminAddCoownerDialog.vue";

export enum OwnershipType {
  Dog = 1,
  Lease = 2,
  Litter = 3,
  Kennel = 4,
}

export default Vue.extend({
  name: "AdminEditableCoownersList",
  data: function () {
    return {
      peopleSearchDialog: false,
    };
  },
  components: {
    AdminAddCoownerDialog,
  },
  computed: {
    primaryLessee: function () {
      const idx = this.owners.findIndex((object: any) => {
        return object.isPrimary;
      });
      if (idx >= 0) {
        return (this as any).owners[idx].personId;
      }
      return 0
    },
  },
  methods: {
    changePrimaryLessee(personId: number) {
      const index = this.owners.map((c: any) => c.personId).indexOf(personId);
      this.owners.forEach((co: any) => (co.isPrimary = false));
      (this as any).owners[index].isPrimary = true;
      this.$emit("updateSelectedPeople", { newPeople: this.owners });
    },
    changeSelectedPeopleInfo: function (newPeople: any) {
      this.$emit("updateSelectedPeople", { newPeople: newPeople });
    },
    toggleModal: function () {
      this.$data.peopleSearchDialog = !this.$data.peopleSearchDialog;
    },
    title(ownershipType: OwnershipType) {
      if (ownershipType == OwnershipType.Lease) {
        return "Lessee";
      } else if(ownershipType == OwnershipType.Litter) {
        return "Litter owner";  
      } else {
        return "Owner";
      }
    },
    tooltipText(ownershipType: OwnershipType) {
      if (ownershipType == OwnershipType.Lease) {
        return "Only the primary lessee can register litters, release and register puppies. They will also be a primary contact person for the ASCA office.";
      }
      if (ownershipType == OwnershipType.Dog) {
        return "Only the primary owner can register litters, and lease the dog. They will also be a primary contact person for the ASCA office.";
      }
      if (ownershipType == OwnershipType.Kennel) {
        return "Only the primary owner can request kennel ownership changes. They will also be a primary contact person for the ASCA office.";
      }
      if (ownershipType == OwnershipType.Litter) {
        return "Only the primary owner can register and release puppies from the litter. They will also be a primary contact person for the ASCA office.";
      }
      return "";
    },
  },
  props: {
    isReadOnlyMode: { type: Boolean },
    isEditMode: { type: Boolean },
    hideHeader: { type: Boolean },
    ownershipType: { type: Number },
    owners: { type: Array },
    hideContactInfo: { type: Boolean },
  },
});
</script>
